import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Home, ShoppingCart, User, LogOut, Shield, Plus, Trash2, Tag, Users, Clock, 
  CheckCircle, XCircle, DollarSign, Percent, Calendar, Hash, Pencil, X, Gift, CreditCard, Send, Copy, ChevronDown, ChevronUp
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function AdminPromoCodes() {
  const { user, logout, getAuthHeader } = useAuth();
  const [promoCodes, setPromoCodes] = useState([]);
  const [referralCodes, setReferralCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [showReferralForm, setShowReferralForm] = useState(false);
  
  // Gift Card state
  const [giftCards, setGiftCards] = useState([]);
  const [giftCardStats, setGiftCardStats] = useState({ total_issued: 0, total_redeemed: 0, total_outstanding: 0, count: 0 });
  const [showGiftCardForm, setShowGiftCardForm] = useState(false);
  const [giftCardAmount, setGiftCardAmount] = useState('50');
  const [giftCardCustomAmount, setGiftCardCustomAmount] = useState('');
  const [giftCardRecipientEmail, setGiftCardRecipientEmail] = useState('');
  const [giftCardRecipientName, setGiftCardRecipientName] = useState('');
  const [giftCardSenderName, setGiftCardSenderName] = useState('');
  const [giftCardMessage, setGiftCardMessage] = useState('');
  const [creatingGiftCard, setCreatingGiftCard] = useState(false);
  const [giftCardProductExists, setGiftCardProductExists] = useState(false);
  const [settingUpGiftCardProduct, setSettingUpGiftCardProduct] = useState(false);
  const [expandedGiftCards, setExpandedGiftCards] = useState({});
  
  // Edit modal state
  const [editingPromo, setEditingPromo] = useState(null);
  const [editingReferral, setEditingReferral] = useState(null);
  
  // Promo code form
  const [promoCode, setPromoCode] = useState('');
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [promoExpiryOption, setPromoExpiryOption] = useState('permanent');
  const [promoCustomExpiry, setPromoCustomExpiry] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [minOrderTotal, setMinOrderTotal] = useState('');
  
  // Referral code form
  const [referralCode, setReferralCode] = useState('');
  const [teamRiderName, setTeamRiderName] = useState('');
  const [teamRiderEmail, setTeamRiderEmail] = useState('');
  const [referralExpiryOption, setReferralExpiryOption] = useState('permanent');
  const [referralCustomExpiry, setReferralCustomExpiry] = useState('');

  // Helper to calculate expiry date from option
  const calculateExpiryDate = (option, customDate) => {
    if (option === 'permanent') return null;
    if (option === 'custom') return customDate || null;
    
    const now = new Date();
    switch (option) {
      case '1hour':
        now.setHours(now.getHours() + 1);
        break;
      case '24hours':
        now.setHours(now.getHours() + 24);
        break;
      case '1week':
        now.setDate(now.getDate() + 7);
        break;
      case '1month':
        now.setMonth(now.getMonth() + 1);
        break;
      case '1year':
        now.setFullYear(now.getFullYear() + 1);
        break;
      default:
        return null;
    }
    return now.toISOString();
  };

  useEffect(() => {
    fetchCodes();
    fetchGiftCards();
    checkGiftCardProduct();
  }, []);

  const fetchCodes = async () => {
    try {
      const [promoRes, referralRes] = await Promise.all([
        fetch(`${API}/api/admin/promo-codes`, { headers: getAuthHeader() }),
        fetch(`${API}/api/admin/referral-codes`, { headers: getAuthHeader() })
      ]);
      
      if (promoRes.ok) {
        const promoData = await promoRes.json();
        setPromoCodes(promoData);
      }
      
      if (referralRes.ok) {
        const referralData = await referralRes.json();
        setReferralCodes(referralData);
      }
    } catch (error) {
      console.error('Failed to fetch codes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Gift Card Functions
  const fetchGiftCards = async () => {
    try {
      const res = await fetch(`${API}/api/admin/gift-cards`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setGiftCards(data.gift_cards || []);
        setGiftCardStats(data.stats || { total_issued: 0, total_redeemed: 0, total_outstanding: 0, count: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch gift cards:', error);
    }
  };

  const checkGiftCardProduct = async () => {
    try {
      const res = await fetch(`${API}/api/products`, { headers: getAuthHeader() });
      if (res.ok) {
        const products = await res.json();
        const giftCardProduct = products.find(p => p.is_gift_card === true);
        setGiftCardProductExists(!!giftCardProduct);
      }
    } catch (error) {
      console.error('Failed to check gift card product:', error);
    }
  };

  const setupGiftCardProduct = async () => {
    setSettingUpGiftCardProduct(true);
    try {
      const res = await fetch(`${API}/api/admin/gift-cards/setup-product`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('GIFT CARD PRODUCT CREATED');
        setGiftCardProductExists(true);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'FAILED TO CREATE PRODUCT');
      }
    } catch (error) {
      toast.error('FAILED TO CREATE PRODUCT');
    } finally {
      setSettingUpGiftCardProduct(false);
    }
  };

  const createGiftCard = async (e) => {
    e.preventDefault();
    const amount = giftCardAmount === 'custom' ? parseFloat(giftCardCustomAmount) : parseFloat(giftCardAmount);
    
    if (!amount || amount <= 0) {
      toast.error('PLEASE ENTER A VALID AMOUNT');
      return;
    }
    
    setCreatingGiftCard(true);
    try {
      const res = await fetch(`${API}/api/admin/gift-cards`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount,
          recipient_email: giftCardRecipientEmail || null,
          recipient_name: giftCardRecipientName || null,
          sender_name: giftCardSenderName || null,
          message: giftCardMessage || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`GIFT CARD CREATED: ${data.code}`);
        setShowGiftCardForm(false);
        setGiftCardAmount('50');
        setGiftCardCustomAmount('');
        setGiftCardRecipientEmail('');
        setGiftCardRecipientName('');
        setGiftCardSenderName('');
        setGiftCardMessage('');
        fetchGiftCards();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'FAILED TO CREATE GIFT CARD');
      }
    } catch (error) {
      toast.error('FAILED TO CREATE GIFT CARD');
    } finally {
      setCreatingGiftCard(false);
    }
  };

  const deactivateGiftCard = async (giftCardId) => {
    if (!window.confirm('DEACTIVATE THIS GIFT CARD?')) return;
    
    try {
      const res = await fetch(`${API}/api/admin/gift-cards/${giftCardId}/deactivate`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      
      if (res.ok) {
        toast.success('GIFT CARD DEACTIVATED');
        fetchGiftCards();
      } else {
        toast.error('FAILED TO DEACTIVATE GIFT CARD');
      }
    } catch (error) {
      toast.error('FAILED TO DEACTIVATE GIFT CARD');
    }
  };

  const reactivateGiftCard = async (giftCardId) => {
    try {
      const res = await fetch(`${API}/api/admin/gift-cards/${giftCardId}/reactivate`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      
      if (res.ok) {
        toast.success('GIFT CARD REACTIVATED');
        fetchGiftCards();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'FAILED TO REACTIVATE GIFT CARD');
      }
    } catch (error) {
      toast.error('FAILED TO REACTIVATE GIFT CARD');
    }
  };

  const deleteGiftCard = async (giftCardId) => {
    if (!window.confirm('DELETE THIS GIFT CARD? This cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API}/api/admin/gift-cards/${giftCardId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (res.ok) {
        toast.success('GIFT CARD DELETED');
        fetchGiftCards();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'FAILED TO DELETE GIFT CARD');
      }
    } catch (error) {
      toast.error('FAILED TO DELETE GIFT CARD');
    }
  };

  const copyGiftCardCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('CODE COPIED');
  };

  const toggleGiftCardExpand = (gcId) => {
    setExpandedGiftCards(prev => ({
      ...prev,
      [gcId]: !prev[gcId]
    }));
  };

  const createPromoCode = async (e) => {
    e.preventDefault();
    
    if (!promoCode || !discountValue) {
      toast.error('CODE AND DISCOUNT VALUE REQUIRED');
      return;
    }
    
    try {
      const expiryDate = calculateExpiryDate(promoExpiryOption, promoCustomExpiry);
      
      const response = await fetch(`${API}/api/admin/promo-codes`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: promoCode,
          discount_type: discountType,
          discount_value: parseFloat(discountValue),
          expires_at: expiryDate,
          max_uses: maxUses ? parseInt(maxUses) : null,
          min_order_total: minOrderTotal ? parseFloat(minOrderTotal) : 0
        })
      });
      
      if (response.ok) {
        toast.success('PROMO CODE CREATED');
        setShowPromoForm(false);
        setPromoCode('');
        setDiscountValue('');
        setPromoExpiryOption('permanent');
        setPromoCustomExpiry('');
        setMaxUses('');
        setMinOrderTotal('');
        fetchCodes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'FAILED TO CREATE PROMO CODE');
      }
    } catch (error) {
      toast.error('FAILED TO CREATE PROMO CODE');
    }
  };

  const createReferralCode = async (e) => {
    e.preventDefault();
    
    if (!referralCode || !teamRiderName) {
      toast.error('CODE AND TEAM RIDER NAME REQUIRED');
      return;
    }
    
    try {
      const expiryDate = calculateExpiryDate(referralExpiryOption, referralCustomExpiry);
      
      const response = await fetch(`${API}/api/admin/referral-codes`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: referralCode,
          team_rider_name: teamRiderName,
          team_rider_email: teamRiderEmail || null,
          expires_at: expiryDate
        })
      });
      
      if (response.ok) {
        toast.success('REFERRAL CODE CREATED');
        setShowReferralForm(false);
        setReferralCode('');
        setTeamRiderName('');
        setTeamRiderEmail('');
        setReferralExpiryOption('permanent');
        setReferralCustomExpiry('');
        fetchCodes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'FAILED TO CREATE REFERRAL CODE');
      }
    } catch (error) {
      toast.error('FAILED TO CREATE REFERRAL CODE');
    }
  };

  const deletePromoCode = async (codeId) => {
    if (!window.confirm('DELETE THIS PROMO CODE?')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/promo-codes/${codeId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        toast.success('PROMO CODE DELETED');
        fetchCodes();
      } else {
        toast.error('FAILED TO DELETE PROMO CODE');
      }
    } catch (error) {
      toast.error('FAILED TO DELETE PROMO CODE');
    }
  };

  const deleteReferralCode = async (codeId) => {
    if (!window.confirm('DELETE THIS REFERRAL CODE?')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/referral-codes/${codeId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        toast.success('REFERRAL CODE DELETED');
        fetchCodes();
      } else {
        toast.error('FAILED TO DELETE REFERRAL CODE');
      }
    } catch (error) {
      toast.error('FAILED TO DELETE REFERRAL CODE');
    }
  };

  // Edit promo code
  const updatePromoCode = async (e) => {
    e.preventDefault();
    if (!editingPromo) return;
    
    try {
      const expiryDate = editingPromo.expiryOption === 'custom' 
        ? editingPromo.customExpiry 
        : editingPromo.expiryOption === 'permanent' 
          ? null 
          : calculateExpiryDate(editingPromo.expiryOption, null);
      
      const response = await fetch(`${API}/api/admin/promo-codes/${editingPromo.id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: (editingPromo.code || '').toUpperCase().trim(),
          discount_type: editingPromo.discount_type,
          discount_value: parseFloat(editingPromo.discount_value),
          expires_at: expiryDate,
          max_uses: editingPromo.max_uses ? parseInt(editingPromo.max_uses) : null,
          min_order_total: editingPromo.min_order_total ? parseFloat(editingPromo.min_order_total) : 0
        })
      });
      
      if (response.ok) {
        toast.success('PROMO CODE UPDATED');
        setEditingPromo(null);
        fetchCodes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'FAILED TO UPDATE');
      }
    } catch (error) {
      toast.error('FAILED TO UPDATE PROMO CODE');
    }
  };

  // Edit referral code
  const updateReferralCode = async (e) => {
    e.preventDefault();
    if (!editingReferral) return;
    
    try {
      const expiryDate = editingReferral.expiryOption === 'custom' 
        ? editingReferral.customExpiry 
        : editingReferral.expiryOption === 'permanent' 
          ? null 
          : calculateExpiryDate(editingReferral.expiryOption, null);
      
      const response = await fetch(`${API}/api/admin/referral-codes/${editingReferral.id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: (editingReferral.code || '').toUpperCase().trim(),
          team_rider_name: editingReferral.team_rider_name,
          team_rider_email: editingReferral.team_rider_email || null,
          expires_at: expiryDate
        })
      });
      
      if (response.ok) {
        toast.success('REFERRAL CODE UPDATED');
        setEditingReferral(null);
        fetchCodes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'FAILED TO UPDATE');
      }
    } catch (error) {
      toast.error('FAILED TO UPDATE REFERRAL CODE');
    }
  };

  // Open edit modal for promo code
  const openEditPromo = (code) => {
    const expiryOption = code.expires_at ? 'custom' : 'permanent';
    setEditingPromo({
      ...code,
      expiryOption,
      customExpiry: code.expires_at ? code.expires_at.slice(0, 16) : ''
    });
  };

  // Open edit modal for referral code
  const openEditReferral = (code) => {
    const expiryOption = code.expires_at ? 'custom' : 'permanent';
    setEditingReferral({
      ...code,
      expiryOption,
      customExpiry: code.expires_at ? code.expires_at.slice(0, 16) : ''
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'NEVER';
    return new Date(dateStr).toLocaleDateString();
  };

  const isExpired = (code) => {
    if (!code.expires_at) return false;
    return new Date(code.expires_at) < new Date();
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-promo-codes-page">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-[#050505]/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          
          <nav className="flex items-center gap-4">
            <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <ShoppingCart className="w-5 h-5" />
            </Link>
            <Link to="/dashboard" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <User className="w-5 h-5" />
            </Link>
            <Link to="/admin" className="p-2 text-tiffany-500">
              <Shield className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-mono text-2xl text-white">PROMO & REFERRAL CODES</h1>
          <Link to="/admin" className="font-mono text-sm text-zinc-400 hover:text-tiffany-500">
            ← BACK TO ADMIN
          </Link>
        </div>

        {/* Promo Codes Section */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-mono text-lg text-tiffany-500 flex items-center gap-2">
              <Tag className="w-5 h-5" />
              PROMO CODES
            </h2>
            <button
              onClick={() => setShowPromoForm(!showPromoForm)}
              className="flex items-center gap-2 bg-tiffany-500 text-black font-mono text-xs px-4 py-2 hover:bg-tiffany-400 transition-colors"
            >
              <Plus className="w-4 h-4" />
              CREATE PROMO CODE
            </button>
          </div>

          {/* Create Promo Form */}
          {showPromoForm && (
            <form onSubmit={createPromoCode} className="bg-zinc-900 border border-zinc-700 p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">CODE</label>
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    placeholder="BLKFRDY"
                    data-testid="promo-code-input"
                  />
                </div>
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">DISCOUNT TYPE</label>
                  <select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none rounded-none appearance-none"
                    style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                    data-testid="discount-type-select"
                  >
                    <option value="percent">PERCENT OFF</option>
                    <option value="fixed">FIXED AMOUNT</option>
                  </select>
                </div>
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">
                    {discountType === 'percent' ? 'PERCENT' : 'AMOUNT ($)'}
                  </label>
                  <input
                    type="number"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    placeholder={discountType === 'percent' ? '25' : '10.00'}
                    min="0"
                    step={discountType === 'percent' ? '1' : '0.01'}
                    data-testid="discount-value-input"
                  />
                </div>
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">EXPIRES</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: '1hour', label: '1 HR' },
                      { value: '24hours', label: '24 HR' },
                      { value: '1week', label: '1 WK' },
                      { value: '1month', label: '1 MO' },
                      { value: '1year', label: '1 YR' },
                      { value: 'permanent', label: 'NEVER' },
                      { value: 'custom', label: 'CUSTOM' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setPromoExpiryOption(opt.value);
                          if (opt.value === 'custom' && !promoCustomExpiry) {
                            // Default to 1 week from now
                            const d = new Date();
                            d.setDate(d.getDate() + 7);
                            setPromoCustomExpiry(d.toISOString().slice(0, 16));
                          }
                        }}
                        className={`px-3 py-1.5 font-mono text-xs border transition-colors ${
                          promoExpiryOption === opt.value
                            ? 'bg-tiffany-500 text-black border-tiffany-500'
                            : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'
                        }`}
                        data-testid={`promo-expiry-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {promoExpiryOption === 'custom' && (
                  <div>
                    <label className="font-mono text-xs text-zinc-500 block mb-1">CUSTOM DATE</label>
                    <input
                      type="datetime-local"
                      value={promoCustomExpiry}
                      onChange={(e) => setPromoCustomExpiry(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                      data-testid="promo-custom-date-input"
                    />
                  </div>
                )}
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">MAX USES (OPTIONAL)</label>
                  <input
                    type="number"
                    value={maxUses}
                    onChange={(e) => setMaxUses(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    placeholder="UNLIMITED"
                    min="1"
                    data-testid="max-uses-input"
                  />
                </div>
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">MIN ORDER $ (OPTIONAL)</label>
                  <input
                    type="number"
                    value={minOrderTotal}
                    onChange={(e) => setMinOrderTotal(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    data-testid="min-order-input"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  type="submit"
                  className="bg-tiffany-500 text-black font-mono text-xs px-6 py-2 hover:bg-tiffany-400 transition-colors"
                  data-testid="create-promo-btn"
                >
                  CREATE
                </button>
                <button
                  type="button"
                  onClick={() => setShowPromoForm(false)}
                  className="bg-zinc-700 text-white font-mono text-xs px-6 py-2 hover:bg-zinc-600 transition-colors"
                >
                  CANCEL
                </button>
              </div>
            </form>
          )}

          {/* Promo Codes List */}
          <div className="space-y-3">
            {promoCodes.length === 0 ? (
              <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                <Tag className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="font-mono text-sm text-zinc-500">NO PROMO CODES</p>
              </div>
            ) : (
              promoCodes.map((code) => (
                <div 
                  key={code.id} 
                  className={`bg-zinc-900 border p-4 ${isExpired(code) ? 'border-red-500/30' : 'border-zinc-700'}`}
                  data-testid={`promo-code-${code.code}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xl text-white font-bold truncate">{code.code}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {code.discount_type === 'percent' ? (
                          <Badge className="bg-purple-500/20 text-purple-400 border-0 font-mono text-xs">
                            <Percent className="w-3 h-3 mr-1" />
                            {code.discount_value}% OFF
                          </Badge>
                        ) : (
                          <Badge className="bg-green-500/20 text-green-400 border-0 font-mono text-xs">
                            <DollarSign className="w-3 h-3 mr-1" />
                            ${code.discount_value} OFF
                          </Badge>
                        )}
                        
                        {isExpired(code) ? (
                          <Badge className="bg-red-500/20 text-red-400 border-0 font-mono text-xs">
                            <XCircle className="w-3 h-3 mr-1" />
                            EXPIRED
                          </Badge>
                        ) : (
                          <Badge className="bg-green-500/20 text-green-400 border-0 font-mono text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            ACTIVE
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Action buttons - always visible */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEditPromo(code)}
                        className="p-2 text-zinc-500 hover:text-tiffany-500 transition-colors"
                        data-testid={`edit-promo-${code.code}`}
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      
                      <button
                        onClick={() => deletePromoCode(code.id)}
                        className="p-2 text-zinc-500 hover:text-red-500 transition-colors"
                        data-testid={`delete-promo-${code.code}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Stats row - below on mobile */}
                  <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-zinc-800">
                    <div className="text-center min-w-[50px]">
                      <p className="font-mono text-[10px] text-zinc-500">USES</p>
                      <p className="font-mono text-white text-sm">
                        {code.use_count || 0}
                        {code.max_uses ? (
                          <span className="text-zinc-500">{` / ${code.max_uses}`}</span>
                        ) : (
                          <span className="text-tiffany-500 text-[10px] tracking-wider">{' / UNLIMITED'}</span>
                        )}
                      </p>
                    </div>
                    
                    <div className="text-center min-w-[60px]">
                      <p className="font-mono text-[10px] text-zinc-500">EXPIRES</p>
                      <p className="font-mono text-zinc-400 text-sm">{formatDate(code.expires_at)}</p>
                    </div>
                    
                    <div className="text-center min-w-[60px]">
                      <p className="font-mono text-[10px] text-zinc-500">SAVINGS</p>
                      <p className="font-mono text-tiffany-500 text-sm">${(code.total_discount_given || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Referral Codes Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-mono text-lg text-tiffany-500 flex items-center gap-2">
              <Users className="w-5 h-5" />
              TEAM RIDER REFERRAL CODES
            </h2>
            <button
              onClick={() => setShowReferralForm(!showReferralForm)}
              className="flex items-center gap-2 bg-tiffany-500 text-black font-mono text-xs px-4 py-2 hover:bg-tiffany-400 transition-colors"
            >
              <Plus className="w-4 h-4" />
              CREATE REFERRAL CODE
            </button>
          </div>

          {/* Create Referral Form */}
          {showReferralForm && (
            <form onSubmit={createReferralCode} className="bg-zinc-900 border border-zinc-700 p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">CODE</label>
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    placeholder="TEAMRIDER"
                    data-testid="referral-code-input"
                  />
                </div>
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">TEAM RIDER NAME</label>
                  <input
                    type="text"
                    value={teamRiderName}
                    onChange={(e) => setTeamRiderName(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    placeholder="RIDER NAME"
                    data-testid="team-rider-name-input"
                  />
                </div>
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">EMAIL (OPTIONAL)</label>
                  <input
                    type="email"
                    value={teamRiderEmail}
                    onChange={(e) => setTeamRiderEmail(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    placeholder="RIDER@EMAIL.COM"
                    data-testid="team-rider-email-input"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="font-mono text-xs text-zinc-500 block mb-1">EXPIRES</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: '1hour', label: '1 HR' },
                    { value: '24hours', label: '24 HR' },
                    { value: '1week', label: '1 WK' },
                    { value: '1month', label: '1 MO' },
                    { value: '1year', label: '1 YR' },
                    { value: 'permanent', label: 'NEVER' },
                    { value: 'custom', label: 'CUSTOM' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setReferralExpiryOption(opt.value);
                        if (opt.value === 'custom' && !referralCustomExpiry) {
                          const d = new Date();
                          d.setDate(d.getDate() + 7);
                          setReferralCustomExpiry(d.toISOString().slice(0, 16));
                        }
                      }}
                      className={`px-3 py-1.5 font-mono text-xs border transition-colors ${
                        referralExpiryOption === opt.value
                          ? 'bg-tiffany-500 text-black border-tiffany-500'
                          : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'
                      }`}
                      data-testid={`referral-expiry-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {referralExpiryOption === 'custom' && (
                <div className="mt-4">
                  <label className="font-mono text-xs text-zinc-500 block mb-1">CUSTOM DATE</label>
                  <input
                    type="datetime-local"
                    value={referralCustomExpiry}
                    onChange={(e) => setReferralCustomExpiry(e.target.value)}
                    className="w-full md:w-1/3 bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    data-testid="referral-custom-date-input"
                  />
                </div>
              )}
              
              <p className="font-mono text-xs text-zinc-500 mt-4">
                REFERRAL CODES GIVE CUSTOMERS 10% OFF THEIR FIRST ORDER. TEAM RIDER EARNS 10% STORE CREDIT ON EACH SALE.
              </p>
              
              <div className="flex gap-3 mt-4">
                <button
                  type="submit"
                  className="bg-tiffany-500 text-black font-mono text-xs px-6 py-2 hover:bg-tiffany-400 transition-colors"
                  data-testid="create-referral-btn"
                >
                  CREATE
                </button>
                <button
                  type="button"
                  onClick={() => setShowReferralForm(false)}
                  className="bg-zinc-700 text-white font-mono text-xs px-6 py-2 hover:bg-zinc-600 transition-colors"
                >
                  CANCEL
                </button>
              </div>
            </form>
          )}

          {/* Referral Codes List */}
          <div className="space-y-3">
            {referralCodes.length === 0 ? (
              <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="font-mono text-sm text-zinc-500">NO REFERRAL CODES</p>
              </div>
            ) : (
              referralCodes.map((code) => (
                <div 
                  key={code.id} 
                  className={`bg-zinc-900 border p-4 ${isExpired(code) ? 'border-red-500/30' : 'border-zinc-700'}`}
                  data-testid={`referral-code-${code.code}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xl text-white font-bold truncate">{code.code}</p>
                      <p className="font-mono text-sm text-tiffany-500 truncate">{code.team_rider_name}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge className="bg-blue-500/20 text-blue-400 border-0 font-mono text-xs">
                          10% OFF FIRST ORDER
                        </Badge>
                        
                        {isExpired(code) ? (
                          <Badge className="bg-red-500/20 text-red-400 border-0 font-mono text-xs">
                            <XCircle className="w-3 h-3 mr-1" />
                            EXPIRED
                          </Badge>
                        ) : (
                          <Badge className="bg-green-500/20 text-green-400 border-0 font-mono text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            ACTIVE
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Action buttons - always visible */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEditReferral(code)}
                        className="p-2 text-zinc-500 hover:text-tiffany-500 transition-colors"
                        data-testid={`edit-referral-${code.code}`}
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      
                      <button
                        onClick={() => deleteReferralCode(code.id)}
                        className="p-2 text-zinc-500 hover:text-red-500 transition-colors"
                        data-testid={`delete-referral-${code.code}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Stats row - wraps on mobile */}
                  <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-zinc-800">
                    <div className="text-center min-w-[40px]">
                      <p className="font-mono text-[10px] text-zinc-500">USES</p>
                      <p className="font-mono text-white text-sm">{code.use_count || 0}</p>
                    </div>
                    
                    <div className="text-center min-w-[70px]">
                      <p className="font-mono text-[10px] text-zinc-500">SALES</p>
                      <p className="font-mono text-white text-sm">${(code.total_sales || 0).toFixed(2)}</p>
                    </div>
                    
                    <div className="text-center min-w-[60px]">
                      <p className="font-mono text-[10px] text-zinc-500">CREDIT</p>
                      <p className="font-mono text-tiffany-500 text-sm">${(code.total_commission_earned || 0).toFixed(2)}</p>
                    </div>
                    
                    <div className="text-center min-w-[60px]">
                      <p className="font-mono text-[10px] text-zinc-500">EXPIRES</p>
                      <p className="font-mono text-zinc-400 text-sm">{formatDate(code.expires_at)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Gift Cards Section */}
        <section className="mb-12 mt-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-mono text-lg text-tiffany-500 flex items-center gap-2">
              <Gift className="w-5 h-5" />
              GIFT CARDS
            </h2>
            <div className="flex items-center gap-2">
              {!giftCardProductExists && (
                <button
                  onClick={setupGiftCardProduct}
                  disabled={settingUpGiftCardProduct}
                  className="flex items-center gap-2 bg-amber-500 text-black font-mono text-xs px-4 py-2 hover:bg-amber-400 transition-colors disabled:opacity-50"
                  data-testid="setup-gift-card-product-btn"
                >
                  <CreditCard className="w-4 h-4" />
                  {settingUpGiftCardProduct ? 'SETTING UP...' : 'SETUP SHOP PRODUCT'}
                </button>
              )}
              <button
                onClick={() => setShowGiftCardForm(!showGiftCardForm)}
                className="flex items-center gap-2 bg-tiffany-500 text-black font-mono text-xs px-4 py-2 hover:bg-tiffany-400 transition-colors"
                data-testid="create-gift-card-btn"
              >
                <Plus className="w-4 h-4" />
                CREATE GIFT CARD
              </button>
            </div>
          </div>

          {/* Setup Product Notice */}
          {!giftCardProductExists && (
            <div className="bg-amber-500/10 border border-amber-500/30 p-4 mb-6">
              <p className="font-mono text-sm text-amber-400">
                <CreditCard className="w-4 h-4 inline mr-2" />
                GIFT CARD PRODUCT NOT FOUND IN SHOP
              </p>
              <p className="font-mono text-xs text-zinc-400 mt-1">
                CLICK "SETUP SHOP PRODUCT" TO ADD GIFT CARDS TO YOUR CATALOG
              </p>
            </div>
          )}

          {/* Gift Card Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-zinc-900 border border-zinc-700 p-3 text-center">
              <p className="font-mono text-[10px] text-zinc-500">TOTAL ISSUED</p>
              <p className="font-mono text-xl text-tiffany-500">${giftCardStats.total_issued.toFixed(2)}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-700 p-3 text-center">
              <p className="font-mono text-[10px] text-zinc-500">REDEEMED</p>
              <p className="font-mono text-xl text-green-500">${giftCardStats.total_redeemed.toFixed(2)}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-700 p-3 text-center">
              <p className="font-mono text-[10px] text-zinc-500">OUTSTANDING</p>
              <p className="font-mono text-xl text-amber-500">${giftCardStats.total_outstanding.toFixed(2)}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-700 p-3 text-center">
              <p className="font-mono text-[10px] text-zinc-500">CARDS</p>
              <p className="font-mono text-xl text-white">{giftCardStats.count}</p>
            </div>
          </div>

          {/* Create Gift Card Form */}
          {showGiftCardForm && (
            <form onSubmit={createGiftCard} className="bg-zinc-900 border border-zinc-700 p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-2">AMOUNT</label>
                  <div className="flex flex-wrap gap-2">
                    {['25', '50', '100', 'custom'].map(amt => (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => setGiftCardAmount(amt)}
                        className={`px-4 py-2 font-mono text-sm border transition-colors ${
                          giftCardAmount === amt
                            ? 'bg-tiffany-500 text-black border-tiffany-500'
                            : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'
                        }`}
                        data-testid={`gift-card-amount-${amt}`}
                      >
                        {amt === 'custom' ? 'CUSTOM' : `$${amt}`}
                      </button>
                    ))}
                  </div>
                  {giftCardAmount === 'custom' && (
                    <input
                      type="number"
                      value={giftCardCustomAmount}
                      onChange={(e) => setGiftCardCustomAmount(e.target.value)}
                      placeholder="Enter amount..."
                      min="1"
                      max="500"
                      className="mt-2 w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                      data-testid="gift-card-custom-amount"
                    />
                  )}
                </div>
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">RECIPIENT EMAIL (OPTIONAL)</label>
                  <input
                    type="email"
                    value={giftCardRecipientEmail}
                    onChange={(e) => setGiftCardRecipientEmail(e.target.value)}
                    placeholder="recipient@email.com"
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    data-testid="gift-card-recipient-email"
                  />
                  <p className="font-mono text-[10px] text-zinc-600 mt-1 uppercase">IF PROVIDED, GIFT CARD WILL BE EMAILED TO RECIPIENT</p>
                </div>
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">RECIPIENT NAME (OPTIONAL)</label>
                  <input
                    type="text"
                    value={giftCardRecipientName}
                    onChange={(e) => setGiftCardRecipientName(e.target.value)}
                    placeholder="RECIPIENT NAME"
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    data-testid="gift-card-recipient-name"
                  />
                </div>
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">SENDER NAME (OPTIONAL)</label>
                  <input
                    type="text"
                    value={giftCardSenderName}
                    onChange={(e) => setGiftCardSenderName(e.target.value)}
                    placeholder="FROM (DEFAULTS TO BLK MRKT)"
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    data-testid="gift-card-sender-name"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="font-mono text-xs text-zinc-500 block mb-1">MESSAGE (OPTIONAL)</label>
                  <textarea
                    value={giftCardMessage}
                    onChange={(e) => setGiftCardMessage(e.target.value)}
                    placeholder="PERSONAL MESSAGE TO INCLUDE..."
                    rows={2}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none resize-none"
                    data-testid="gift-card-message"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  type="submit"
                  disabled={creatingGiftCard}
                  className="bg-tiffany-500 text-black font-mono text-xs px-6 py-2 hover:bg-tiffany-400 transition-colors disabled:opacity-50"
                  data-testid="submit-gift-card-btn"
                >
                  {creatingGiftCard ? 'CREATING...' : 'CREATE GIFT CARD'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowGiftCardForm(false)}
                  className="bg-zinc-700 text-white font-mono text-xs px-6 py-2 hover:bg-zinc-600 transition-colors"
                >
                  CANCEL
                </button>
              </div>
            </form>
          )}

          {/* Gift Cards List */}
          <div className="space-y-2">
            {giftCards.length === 0 ? (
              <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                <Gift className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="font-mono text-sm text-zinc-500">NO GIFT CARDS</p>
              </div>
            ) : (
              giftCards.map((gc) => (
                <div 
                  key={gc.id} 
                  className={`bg-zinc-900 border ${
                    gc.status === 'deactivated' ? 'border-red-500/30' : 
                    gc.status === 'depleted' ? 'border-zinc-600' :
                    'border-tiffany-500/30'
                  }`}
                  data-testid={`gift-card-${gc.code}`}
                >
                  {/* Collapsed Header - Always visible */}
                  <button
                    onClick={() => toggleGiftCardExpand(gc.id)}
                    className="w-full p-3 flex items-center justify-between hover:bg-zinc-800/30 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono text-sm text-white font-bold whitespace-nowrap">{gc.code}</span>
                      
                      {/* Status Badge */}
                      {gc.status === 'active' && (
                        <Badge className="bg-green-500/20 text-green-400 border-0 font-mono text-[10px] shrink-0">ACTIVE</Badge>
                      )}
                      {gc.status === 'deactivated' && (
                        <Badge className="bg-red-500/20 text-red-400 border-0 font-mono text-[10px] shrink-0">DEACTIVATED</Badge>
                      )}
                      {gc.status === 'depleted' && (
                        <Badge className="bg-zinc-500/20 text-zinc-400 border-0 font-mono text-[10px] shrink-0">DEPLETED</Badge>
                      )}
                      
                      <span className={`font-mono text-sm shrink-0 ${gc.current_balance > 0 ? 'text-tiffany-500' : 'text-zinc-500'}`}>
                        ${gc.current_balance.toFixed(2)}
                      </span>
                    </div>
                    
                    {expandedGiftCards[gc.id] ? (
                      <ChevronUp className="w-4 h-4 text-zinc-500 shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />
                    )}
                  </button>
                  
                  {/* Expanded Details */}
                  {expandedGiftCards[gc.id] && (
                    <div className="px-3 pb-3 space-y-3">
                      {/* Details Grid */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-zinc-800/50 p-2">
                          <p className="font-mono text-[10px] text-zinc-500">EMAIL</p>
                          <p className="font-mono text-xs text-white break-all">{gc.recipient_email || 'N/A'}</p>
                        </div>
                        <div className="bg-zinc-800/50 p-2">
                          <p className="font-mono text-[10px] text-zinc-500">INITIAL VALUE</p>
                          <p className="font-mono text-sm text-white">${gc.initial_amount.toFixed(2)}</p>
                        </div>
                        <div className="bg-zinc-800/50 p-2">
                          <p className="font-mono text-[10px] text-zinc-500">SPENT</p>
                          <p className="font-mono text-sm text-red-400">${(gc.initial_amount - gc.current_balance).toFixed(2)}</p>
                        </div>
                        <div className="bg-zinc-800/50 p-2">
                          <p className="font-mono text-[10px] text-zinc-500">REMAINING</p>
                          <p className={`font-mono text-sm ${gc.current_balance > 0 ? 'text-tiffany-500' : 'text-zinc-500'}`}>${gc.current_balance.toFixed(2)}</p>
                        </div>
                      </div>
                      
                      {/* Additional Info */}
                      <div className="flex flex-wrap items-center gap-3 text-zinc-500 text-[10px] font-mono">
                        <span>CREATED: {new Date(gc.created_at).toLocaleDateString()}</span>
                        {gc.recipient_name && <span>TO: {gc.recipient_name.toUpperCase()}</span>}
                        {gc.redemption_history?.length > 0 && <span>REDEMPTIONS: {gc.redemption_history.length}</span>}
                        {gc.order_id && <span className="text-tiffany-500">ORDER: {gc.order_id}</span>}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 pt-2 border-t border-zinc-800">
                        <button
                          onClick={(e) => { e.stopPropagation(); copyGiftCardCode(gc.code); }}
                          className="flex items-center gap-1 px-3 py-1 bg-zinc-800 text-zinc-400 hover:text-white font-mono text-xs transition-colors"
                        >
                          <Copy className="w-3 h-3" /> COPY
                        </button>
                        {gc.status === 'active' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); deactivateGiftCard(gc.id); }}
                            className="flex items-center gap-1 px-3 py-1 bg-zinc-800 text-zinc-400 hover:text-red-500 font-mono text-xs transition-colors"
                          >
                            <XCircle className="w-3 h-3" /> DEACTIVATE
                          </button>
                        )}
                        {gc.status === 'deactivated' && gc.current_balance > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); reactivateGiftCard(gc.id); }}
                            className="flex items-center gap-1 px-3 py-1 bg-zinc-800 text-zinc-400 hover:text-green-500 font-mono text-xs transition-colors"
                          >
                            <CheckCircle className="w-3 h-3" /> REACTIVATE
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteGiftCard(gc.id); }}
                          className="flex items-center gap-1 px-3 py-1 bg-zinc-800 text-zinc-400 hover:text-red-500 font-mono text-xs transition-colors"
                        >
                          <Trash2 className="w-3 h-3" /> DELETE
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </section>

        {/* BLKCRD Link - Moved to dedicated page */}
        <section className="border border-purple-500/30 bg-purple-500/5 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-purple-400" />
              <span className="font-mono text-sm text-white">BLKCRD VIP MEMBERS</span>
            </div>
            <Link 
              to="/admin/blkcrd" 
              className="bg-purple-500/20 text-purple-400 font-mono text-xs px-4 py-2 hover:bg-purple-500/30 transition-colors"
            >
              MANAGE BLKCRD →
            </Link>
          </div>
        </section>
      </main>

      {/* Edit Promo Code Modal */}
      {editingPromo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-zinc-700">
              <h3 className="font-mono text-lg text-white">EDIT {editingPromo.code}</h3>
              <button onClick={() => setEditingPromo(null)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={updatePromoCode} className="p-4 space-y-4">
              <div>
                <label className="font-mono text-xs text-zinc-500 block mb-1">CODE</label>
                <input
                  type="text"
                  value={editingPromo.code}
                  onChange={(e) => setEditingPromo({...editingPromo, code: e.target.value.toUpperCase()})}
                  className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none uppercase tracking-wider"
                  minLength={3}
                  maxLength={20}
                  required
                  data-testid="edit-promo-code-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">DISCOUNT TYPE</label>
                  <select
                    value={editingPromo.discount_type}
                    onChange={(e) => setEditingPromo({...editingPromo, discount_type: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none rounded-none appearance-none"
                    style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                  >
                    <option value="percent">PERCENT OFF</option>
                    <option value="fixed">FIXED AMOUNT</option>
                  </select>
                </div>
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">
                    {editingPromo.discount_type === 'percent' ? 'PERCENT' : 'AMOUNT'}
                  </label>
                  <input
                    type="number"
                    value={editingPromo.discount_value}
                    onChange={(e) => setEditingPromo({...editingPromo, discount_value: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    min="0"
                    step={editingPromo.discount_type === 'percent' ? '1' : '0.01'}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">EXPIRES</label>
                  <div className="flex flex-wrap gap-1">
                    {[
                      { value: '1hour', label: '1H' },
                      { value: '24hours', label: '24H' },
                      { value: '1week', label: '1W' },
                      { value: '1month', label: '1M' },
                      { value: '1year', label: '1Y' },
                      { value: 'permanent', label: '∞' },
                      { value: 'custom', label: '...' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          const newExpiry = { ...editingPromo, expiryOption: opt.value };
                          if (opt.value === 'custom' && !editingPromo.customExpiry) {
                            const d = new Date();
                            d.setDate(d.getDate() + 7);
                            newExpiry.customExpiry = d.toISOString().slice(0, 16);
                          }
                          setEditingPromo(newExpiry);
                        }}
                        className={`px-2 py-1 font-mono text-xs border transition-colors ${
                          editingPromo.expiryOption === opt.value
                            ? 'bg-tiffany-500 text-black border-tiffany-500'
                            : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">MAX USES</label>
                  <input
                    type="number"
                    value={editingPromo.max_uses || ''}
                    onChange={(e) => setEditingPromo({...editingPromo, max_uses: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                    placeholder="UNLIMITED"
                    min="1"
                  />
                </div>
              </div>
              
              {editingPromo.expiryOption === 'custom' && (
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">CUSTOM DATE</label>
                  <input
                    type="datetime-local"
                    value={editingPromo.customExpiry}
                    onChange={(e) => setEditingPromo({...editingPromo, customExpiry: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                  />
                </div>
              )}
              
              <div>
                <label className="font-mono text-xs text-zinc-500 block mb-1">MIN ORDER $</label>
                <input
                  type="number"
                  value={editingPromo.min_order_total || ''}
                  onChange={(e) => setEditingPromo({...editingPromo, min_order_total: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-tiffany-500 text-black font-mono text-xs px-6 py-2 hover:bg-tiffany-400 transition-colors"
                >
                  SAVE CHANGES
                </button>
                <button
                  type="button"
                  onClick={() => setEditingPromo(null)}
                  className="bg-zinc-700 text-white font-mono text-xs px-6 py-2 hover:bg-zinc-600 transition-colors"
                >
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Referral Code Modal */}
      {editingReferral && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-zinc-700">
              <h3 className="font-mono text-lg text-white">EDIT {editingReferral.code}</h3>
              <button onClick={() => setEditingReferral(null)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={updateReferralCode} className="p-4 space-y-4">
              <div>
                <label className="font-mono text-xs text-zinc-500 block mb-1">CODE</label>
                <input
                  type="text"
                  value={editingReferral.code}
                  onChange={(e) => setEditingReferral({...editingReferral, code: e.target.value.toUpperCase()})}
                  className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none uppercase tracking-wider"
                  minLength={3}
                  maxLength={20}
                  required
                  data-testid="edit-referral-code-input"
                />
              </div>

              <div>
                <label className="font-mono text-xs text-zinc-500 block mb-1">TEAM RIDER NAME</label>
                <input
                  type="text"
                  value={editingReferral.team_rider_name}
                  onChange={(e) => setEditingReferral({...editingReferral, team_rider_name: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="font-mono text-xs text-zinc-500 block mb-1">EMAIL (OPTIONAL)</label>
                <input
                  type="email"
                  value={editingReferral.team_rider_email || ''}
                  onChange={(e) => setEditingReferral({...editingReferral, team_rider_email: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                  placeholder="RIDER@EMAIL.COM"
                />
              </div>
              
              <div>
                <label className="font-mono text-xs text-zinc-500 block mb-1">EXPIRES</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: '1hour', label: '1 HR' },
                    { value: '24hours', label: '24 HR' },
                    { value: '1week', label: '1 WK' },
                    { value: '1month', label: '1 MO' },
                    { value: '1year', label: '1 YR' },
                    { value: 'permanent', label: 'NEVER' },
                    { value: 'custom', label: 'CUSTOM' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        const newExpiry = { ...editingReferral, expiryOption: opt.value };
                        if (opt.value === 'custom' && !editingReferral.customExpiry) {
                          const d = new Date();
                          d.setDate(d.getDate() + 7);
                          newExpiry.customExpiry = d.toISOString().slice(0, 16);
                        }
                        setEditingReferral(newExpiry);
                      }}
                      className={`px-3 py-1.5 font-mono text-xs border transition-colors ${
                        editingReferral.expiryOption === opt.value
                          ? 'bg-tiffany-500 text-black border-tiffany-500'
                          : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {editingReferral.expiryOption === 'custom' && (
                <div>
                  <label className="font-mono text-xs text-zinc-500 block mb-1">CUSTOM DATE</label>
                  <input
                    type="datetime-local"
                    value={editingReferral.customExpiry}
                    onChange={(e) => setEditingReferral({...editingReferral, customExpiry: e.target.value})}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-2 font-mono text-sm text-white focus:border-tiffany-500 focus:outline-none"
                  />
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-tiffany-500 text-black font-mono text-xs px-6 py-2 hover:bg-tiffany-400 transition-colors"
                >
                  SAVE CHANGES
                </button>
                <button
                  type="button"
                  onClick={() => setEditingReferral(null)}
                  className="bg-zinc-700 text-white font-mono text-xs px-6 py-2 hover:bg-zinc-600 transition-colors"
                >
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
